import { type ReactNode, type HTMLAttributes, createElement } from "react";
import { cn } from "@/lib/utils";

// Typography variants
export type TypographyVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "p"
  | "lead"
  | "large"
  | "small"
  | "muted"
  | "blockquote"
  | "inline-code"
  | "code"
  | "table-head"
  | "table-cell";

interface TypographyProps
  extends Omit<HTMLAttributes<HTMLElement>, "children"> {
  variant: TypographyVariant;
  children: ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

export function Typography({
  variant,
  children,
  className,
  as,
  ...props
}: TypographyProps) {
  // Define the mapping of variants to their default HTML elements and Tailwind classes
  const variantConfig = {
    h1: {
      element: "h1" as const,
      className:
        "font-[800] text-3xl md:text-3xl leading-[1.11em] md:leading-[1em] tracking-[-0.011em] md:tracking-[-0.0083em] ",
    },
    h2: {
      element: "h2" as const,
      className: "font-semibold text-3xl leading-[1.2em] tracking-[-0.0133em] ",
    },
    h3: {
      element: "h3" as const,
      className:
        "font-semibold text-2xl leading-[1.33em] tracking-[-0.0167em] ",
    },
    h4: {
      element: "h4" as const,
      className: "font-semibold text-xl leading-[1.4em] tracking-[-0.02em] ",
    },
    p: {
      element: "p" as const,
      className: "text-base leading-7 ",
    },
    lead: {
      element: "p" as const,
      className: "text-xl leading-7 ",
    },
    large: {
      element: "div" as const,
      className: "font-semibold text-lg leading-7 ",
    },
    small: {
      element: "small" as const,
      className: "font-medium text-sm leading-none ",
    },
    muted: {
      element: "p" as const,
      className: "font-medium text-sm leading-none text-muted-foreground",
    },
    blockquote: {
      element: "blockquote" as const,
      className:
        "mt-6 border-l-2 border-border pl-6 italic text-base leading-6 ",
    },
    "inline-code": {
      element: "code" as const,
      className:
        "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-bold leading-[1.43em] ",
    },
    code: {
      element: "pre" as const,
      className:
        "overflow-x-auto rounded-lg border border-border bg-muted p-4 font-mono text-base leading-7 text-muted-foreground",
    },
    "table-head": {
      element: "th" as const,
      className: "font-bold text-base leading-6 ",
    },
    "table-cell": {
      element: "td" as const,
      className: "text-base leading-6 ",
    },
  } as const;

  const config = variantConfig[variant];
  const Component = (as || config.element) as keyof JSX.IntrinsicElements;

  // Special handling for code variant (needs nested code element)
  if (variant === "code") {
    return createElement(
      Component,
      {
        className: cn(config.className, className),
        ...props,
      },
      createElement("code", {}, children)
    );
  }

  // Regular variants
  return createElement(
    Component,
    {
      className: cn(config.className, className),
      ...props,
    },
    children
  );
}

// List components - keep these separate since they have special props
interface TypographyListProps {
  children: ReactNode;
  className?: string;
  ordered?: boolean;
}

export function TypographyList({
  children,
  className,
  ordered = false,
  ...props
}: TypographyListProps & HTMLAttributes<HTMLUListElement | HTMLOListElement>) {
  if (ordered) {
    return (
      <ol
        className={cn("ml-6 list-decimal text-base leading-7 ", className)}
        {...props}
      >
        {children}
      </ol>
    );
  }

  return (
    <ul
      className={cn("ml-6 list-disc text-base leading-7 ", className)}
      {...props}
    >
      {children}
    </ul>
  );
}

interface TypographyListItemProps {
  children: ReactNode;
  className?: string;
}

export function TypographyListItem({
  children,
  className,
  ...props
}: TypographyListItemProps & HTMLAttributes<HTMLLIElement>) {
  return (
    <li className={cn("text-base leading-7 ", className)} {...props}>
      {children}
    </li>
  );
}

// Convenience components for commonly used variants
export function Heading1(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="h1" {...props} />;
}

export function Heading2(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="h2" {...props} />;
}

export function Heading3(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="h3" {...props} />;
}

export function Heading4(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="h4" {...props} />;
}

export function Text(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="p" {...props} />;
}

export function LeadText(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="lead" {...props} />;
}

export function LargeText(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="large" {...props} />;
}

export function SmallText(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="small" {...props} />;
}

export function MutedText(props: Omit<TypographyProps, "variant">) {
  return <Typography variant="muted" {...props} />;
}
